import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Sms as SmsIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface PhoneVerificationProps {
  onVerified?: () => void;
  initialPhone?: string;
}

const steps = ['Enter Phone Number', 'Enter Verification Code', 'Verified'];

export default function PhoneVerification({ onVerified, initialPhone }: PhoneVerificationProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState(initialPhone || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    checkStatus();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current!);
    }
  }, [countdown]);

  const checkStatus = async () => {
    try {
      const status = await api.get<{
        phoneVerified: boolean;
        phoneNumber?: string;
      }>('/api/registration/verify/status');
      if (status.phoneVerified) {
        setActiveStep(2);
        if (status.phoneNumber) setPhoneNumber(status.phoneNumber);
      }
    } catch {
      // Not authenticated or no status yet
    }
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw.length <= 10) {
      setPhoneNumber(formatPhone(raw));
    }
  };

  const sendCode = async () => {
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await api.post<{ message: string }>('/api/registration/verify/phone/request', {
        phoneNumber: `+1${digits}`,
      });
      setSuccess(result.message || 'Verification code sent!');
      setActiveStep(1);
      setCountdown(60);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (code.length < 4) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await api.post<{ message: string; verificationLevel?: number }>('/api/registration/verify/submit', {
        verificationType: 'phone',
        code,
      });
      setSuccess(result.message || 'Phone verified successfully!');
      setActiveStep(2);
      onVerified?.();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 500 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhoneIcon color="primary" />
        Phone Verification
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {activeStep === 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your phone number to receive a verification code via SMS.
          </Typography>
          <TextField
            fullWidth
            label="Phone Number"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder="(555) 123-4567"
            InputProps={{
              startAdornment: <InputAdornment position="start">+1</InputAdornment>,
            }}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={sendCode}
            disabled={loading || phoneNumber.replace(/\D/g, '').length < 10}
            startIcon={loading ? <CircularProgress size={20} /> : <SmsIcon />}
            fullWidth
          >
            {loading ? 'Sending...' : 'Send Verification Code'}
          </Button>
        </Box>
      )}

      {activeStep === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the 6-digit code sent to {phoneNumber}
          </Typography>
          <TextField
            fullWidth
            label="Verification Code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            inputProps={{ maxLength: 6, style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5rem' } }}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={verifyCode}
            disabled={loading || code.length < 4}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
            fullWidth
            sx={{ mb: 1 }}
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </Button>
          <Button
            variant="text"
            onClick={sendCode}
            disabled={countdown > 0 || loading}
            fullWidth
            size="small"
          >
            {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
          </Button>
          <Button
            variant="text"
            onClick={() => { setActiveStep(0); setCode(''); setError(''); }}
            fullWidth
            size="small"
          >
            Change Phone Number
          </Button>
        </Box>
      )}

      {activeStep === 2 && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
          <Typography variant="h6" color="success.main">
            Phone Verified
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {phoneNumber} has been verified successfully.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
